import { useContext, useEffect, useMemo, useState } from "react";
import { AppContext } from "../AppContextProvider";
import spatialService from "../services/spatialService";
import { sessionCache } from "../utils/sessionCache";
import { readPersisted, writePersisted } from "../utils/persistentCache";

const EMPTY_FC = { type: "FeatureCollection", features: [] };

const pickFC = (resp) => {
  // Support both { data: { data: FC } } envelope and raw FC at .data.
  const body = resp?.data;
  if (body && body.type === "FeatureCollection") return body;
  if (body?.data && body.data.type === "FeatureCollection") return body.data;
  if (Array.isArray(body?.data?.features)) {
    return { type: "FeatureCollection", features: body.data.features };
  }
  return EMPTY_FC;
};

// `${oid}|${endpoint}|${state}|${county}` -> Promise<FeatureCollection>, for
// the life of the tab (utils/sessionCache): the shapes never change while the
// app is open, so closing and reopening a map costs nothing. Holding the
// promise de-dupes requests that overlap in flight.
const cache = sessionCache();

// The shapes almost never change, so the browser also keeps them between
// visits (utils/persistentCache) and a map's first render skips the fetch.
// A day bounds how long a re-loaded grid takes to reach everyone.
const SHAPES_TTL_MS = 24 * 60 * 60 * 1000;

const merge = (fcs) => ({
  type: "FeatureCollection",
  features: fcs.flatMap((fc) => (Array.isArray(fc?.features) ? fc.features : [])),
});

/**
 * Spatial layers for the deals map, for a SET of counties:
 *   states    — once
 *   counties  — once per distinct state in `counties`, merged
 *   sections  — once per (state, county), requested in parallel, merged
 *
 * Every response is cached for the tab's life keyed by (user, endpoint, state,
 * county), so filtering back and forth, or reopening the map, re-uses what
 * was loaded; and kept in the browser for a day, so the next visit's first
 * render fetches nothing either. A 404 counts as "no features" (the backend
 * has no grid for some counties) and is not kept between visits.
 *
 * @param {{ counties?: Array<{ state: string, county: string }> }} [args]
 */
const useSpatialData = ({ counties: wanted = [] } = {}) => {
  const { token, getToken, isTokenExpired, user } = useContext(AppContext);
  const oid = user?.idTokenClaims?.oid;

  const [states, setStates] = useState(null);
  const [counties, setCounties] = useState(null);
  const [sections, setSections] = useState(null);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCounties, setLoadingCounties] = useState(false);
  const [loadingSections, setLoadingSections] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  // Stable keys so effects only re-run when the county set really changes.
  const countyKey = useMemo(
    () =>
      [...new Set((wanted || []).map((c) => `${c.state}|${c.county}`))].sort().join(","),
    [wanted]
  );
  const stateKey = useMemo(
    () => [...new Set((wanted || []).map((c) => c.state))].sort().join(","),
    [wanted]
  );

  const resolveToken = async () => {
    let t = token;
    if (!t || (isTokenExpired && isTokenExpired(t))) t = await getToken();
    return t;
  };

  const cached = (endpointKey, call) => {
    const key = `${oid}|${endpointKey}`;
    if (!cache.has(key)) {
      const p = (async () => {
        const stored = await readPersisted(key);
        if (stored) return stored;
        const fc = pickFC(await call());
        writePersisted(key, fc, SHAPES_TTL_MS); // fire and forget; never throws
        return fc;
      })().catch((err) => {
        if (err?.response?.status === 404) return EMPTY_FC;
        cache.delete(key); // let a later render retry
        throw err;
      });
      cache.set(key, p);
    }
    return cache.get(key);
  };

  // --- States: once ---
  useEffect(() => {
    if (!oid) return undefined;
    let cancelled = false;
    (async () => {
      try {
        setLoadingStates(true);
        const t = await resolveToken();
        const fc = await cached("states||", () => spatialService.getStates(t, oid));
        if (!cancelled) setStates(fc);
      } catch (err) {
        if (!cancelled) setErrorMsg(err?.message || String(err));
      } finally {
        if (!cancelled) setLoadingStates(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oid]);

  // --- Counties: per state, merged ---
  useEffect(() => {
    if (!oid) return undefined;
    if (!stateKey) {
      setCounties(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoadingCounties(true);
        const t = await resolveToken();
        const fcs = await Promise.all(
          stateKey.split(",").map((s) =>
            cached(`counties|${s}|`, () => spatialService.getCounties(t, oid, s))
          )
        );
        if (!cancelled) setCounties(merge(fcs));
      } catch (err) {
        if (!cancelled) setErrorMsg(err?.message || String(err));
      } finally {
        if (!cancelled) setLoadingCounties(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateKey, oid]);

  // --- Sections: per (state, county), in parallel, merged ---
  useEffect(() => {
    if (!oid) return undefined;
    if (!countyKey) {
      setSections(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoadingSections(true);
        setErrorMsg(null);
        const t = await resolveToken();
        const fcs = await Promise.all(
          countyKey.split(",").map((k) => {
            const [state, county] = k.split("|");
            return cached(`sections|${state}|${county}`, () =>
              spatialService.getSections(t, oid, { state, county })
            );
          })
        );
        if (!cancelled) setSections(merge(fcs));
      } catch (err) {
        if (!cancelled) setErrorMsg(err?.message || String(err));
      } finally {
        if (!cancelled) setLoadingSections(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countyKey, oid]);

  const loading = loadingStates || loadingCounties || loadingSections;
  return { states, counties, sections, loading, errorMsg };
};

export default useSpatialData;
